import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { z } from "zod";

import { SubmitButton } from "@/components/buttons/submit-button";
import { EasiRechargeCustomerDataTable } from "@/components/customer-data-table";
import { EasiRechargeDetails } from "@/components/order-details";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";
import { useEasiRechargeMutation } from "@/redux/services/order-portal";
import { useAppSelector } from "@/redux/store";
import { submitOrderSchema } from "@/shared/schemas/easi-recharge/submit-order";

export const SubmitOrderForm = ({ className }: { className?: string }) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const state = useAppSelector((prevState) => prevState.easiRecharge);
  const [easiRecharge] = useEasiRechargeMutation();

  const form = useForm<z.infer<typeof submitOrderSchema>>({
    resolver: zodResolver(submitOrderSchema),
    defaultValues: {
      hasAgreedToTerms: false,
    },
  });

  const hasAgreedToTerms = useWatch({
    control: form.control,
    name: "hasAgreedToTerms",
  });

  const onSubmit: SubmitHandler<
    z.infer<typeof submitOrderSchema>
  > = async () => {
    try {
      if (!session?.token) {
        return;
      }

      const response = await easiRecharge({
        loginToken: session.token,
        mobileNumber: state.mobileNumber,
        optionCode: state.package.code,
      });
      if (response.error) {
        throw new Error("Failed to recharge");
      }

      navigate("/easi-recharge/receipt");

      form.reset();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  };

  return (
    <Form {...form}>
      <form
        className={cn("border", className)}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col lg:flex-row">
          <section className="space-y-6 border p-6 lg:basis-2/3">
            <div className="space-y-6">
              <p className="font-semibold text-lg uppercase">
                {t("summary.titles.customer-data")}
              </p>
              <EasiRechargeCustomerDataTable state={state} />
            </div>
          </section>
          <section className="divide-y border lg:basis-1/3">
            <div className="space-y-6 p-6">
              <p className="font-semibold text-lg uppercase">
                {t("summary.titles.package-summary")}
              </p>
              <EasiRechargeDetails state={state} />
            </div>
          </section>
        </div>

        <div className="flex flex-col lg:flex-row">
          <section className="flex flex-col items-center space-y-6 border p-6 lg:basis-full">
            <FormField
              control={form.control}
              name="hasAgreedToTerms"
              render={({ field }) => (
                <FormItem className="max-w-sm">
                  <div className="flex flex-row items-start gap-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="flex flex-wrap items-baseline gap-x-1">
                        <span>{t("labels.acknowledge-1")}</span>
                        <a
                          className="text-primary underline hover:text-primary/90"
                          href="https://dst.com.bn/legal/"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {t("labels.terms-and-conditions")}
                        </a>
                        <span>{t("labels.and")}</span>
                        <a
                          className="text-primary underline hover:text-primary/90"
                          href="https://dst.com.bn/legal/"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {t("labels.privacy-policy")}
                        </a>
                        <span>{t("labels.acknowledge-2")}</span>
                      </FormLabel>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SubmitButton
              className="rounded-md"
              disabled={!hasAgreedToTerms}
              isSubmitting={form.formState.isSubmitting}
            >
              {t("labels.submit")}
            </SubmitButton>
          </section>
        </div>
      </form>
    </Form>
  );
};
